import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import ErrorBoundary from "./ErrorBoundary";
import { LogOut, KeyRound, LayoutGrid, Users, FileText, Inbox, AlertTriangle, HardDrive, Shield, Boxes, ShoppingCart, BookMarked, ScrollText, ShieldCheck, TrendingUp, FileBarChart, Database, ChevronDown, MessageSquare, FolderOpen, Languages, GraduationCap, ClipboardList, CalendarDays, Wallet, Upload, PieChart, Beaker, MoreHorizontal, Menu, X, HelpCircle, Sparkles, ClipboardCheck, Calendar, Search, Megaphone, Trophy } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useEffect, useRef, useState } from "react";
import useFundEnabled from "../hooks/useFundEnabled";
import SyncPill from "./SyncPill";
import PageBar from "./PageBar";
import HelpPanel from "./HelpPanel";
import AnnouncementBell from "./AnnouncementBell";
import BottomTabBar from "./BottomTabBar";
import GlobalSearch from "./GlobalSearch";
import { api, apiError } from "../lib/api";
import { AVAILABLE_LANGUAGES, t } from "../lib/i18n";
import { toast } from "sonner";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export default function Layout({ children }) {    const { user, logout } = useAuth();
    const location = useLocation();
    const nav = useNavigate();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const stripRef = useRef(null);
    const role = user?.role;
    const effectiveRole = user?.effective_role || role;
    const hasFinancial = effectiveRole === "OWNER" || effectiveRole === "HEAD";
    const hasProcurement = hasFinancial || effectiveRole === "PROCUREMENT_OFFICER";
    const isAdmin = role === "HO_ADMIN";
    const isPM = role === "PROJECT_MANAGER";
    const isClient = effectiveRole === "CLIENT";
    const isPO = effectiveRole === "PROCUREMENT_OFFICER";
    const fund = useFundEnabled();
    const isFH = effectiveRole === "FACILITY_HEAD" || role === "SITE_MANAGER";

    function doLogout() {
        logout();
        nav("/login");
    }

    // ---------- Build the role-aware nav list ----------
    // Order matters: earlier items appear first on desktop primary row.
    const links = [];

    // Landings (role-specific, always first)
    if (isClient) {
        links.push({ to: "/client", label: "My site", icon: Shield, tid: "nav-client-home" });
    } else {
        if (hasFinancial) links.push({ to: "/command", label: "My page", icon: Shield, tid: "nav-command" });
        if (isPO)         links.push({ to: "/procurement", label: "Procurement", icon: ShoppingCart, tid: "nav-procurement" });
        if (hasFinancial) links.push({ to: "/dashboard", label: "Dashboard", icon: TrendingUp, tid: "nav-dashboard" });
        links.push({ to: "/", end: true, label: "Sites", icon: LayoutGrid, tid: "nav-home" });
    }
    // Announcement whiteboard — everyone sees it (clients see only client_visible posts)
    links.push({ to: "/announcements", label: "Board", icon: Megaphone, tid: "nav-announcements" });

    // Empowerment Fund — flag-gated. Field staff at an enabled site see Wallet;
    // everyone internal sees League; FHs of enabled sites get the FH console.
    if (fund.enabled && !isClient) {
        links.push({ to: "/fund/wallet",  label: "Wallet",  icon: Wallet,  tid: "nav-fund-wallet"  });
        links.push({ to: "/fund/league",  label: "League",  icon: Trophy,  tid: "nav-fund-league"  });
        if (isFH || hasFinancial) {
            links.push({ to: "/fund/fh",  label: "Fund · FH", icon: Sparkles, tid: "nav-fund-fh" });
        }
    }

    // Field / cross-site nav — everyone except Client
    if (!isClient) {
        if (!isPO) {
            links.push({ to: "/kra", label: "KRA", icon: FileBarChart, tid: "nav-kra" });
        }
        if (!isPO) {
            links.push({ to: "/training", label: t(user, "nav_training"), icon: GraduationCap, tid: "nav-training" });
            links.push({ to: "/coaching", label: t(user, "nav_coaching"), icon: ClipboardList, tid: "nav-coaching" });
        }
        links.push({ to: "/service-requests", label: "Escalations", icon: AlertTriangle, tid: "nav-service-requests" });
        links.push({ to: "/sla", label: "SLA Lookup", icon: BookMarked, tid: "nav-sla-lookup" });
        links.push({ to: "/compliance", label: t(user, "nav_compliance"), icon: ShieldCheck, tid: "nav-compliance" });
        links.push({ to: "/documents", label: t(user, "nav_documents"), icon: FolderOpen, tid: "nav-documents" });
        if (!isPO) {
            links.push({ to: "/messages", label: t(user, "nav_messages"), icon: MessageSquare, tid: "nav-messages" });
        }
        // Tutorial + Readiness + Leaves + Loans — all internal roles (Client blocked server-side)
        links.push({ to: "/tutorial", label: "Tutorial", icon: Sparkles, tid: "nav-tutorial" });
        links.push({ to: "/readiness", label: "Readiness", icon: ClipboardCheck, tid: "nav-readiness" });
        links.push({ to: "/leaves", label: "Leaves", icon: Calendar, tid: "nav-leaves" });
        links.push({ to: "/loans", label: "Loans", icon: Wallet, tid: "nav-loans" });
        // Assets + Handovers + Visitors (Client excluded above)
        links.push({ to: "/assets", label: "Assets", icon: Boxes, tid: "nav-assets" });
        if (hasFinancial || isPO) {
            links.push({ to: "/handovers", label: "Handovers", icon: FileText, tid: "nav-handovers" });
        }
        if (!isPO) {
            links.push({ to: "/visitors", label: "Visitors", icon: Users, tid: "nav-visitors" });
        }
        if (!isPO) {
            links.push({ to: "/ot", label: "OT", icon: TrendingUp, tid: "nav-ot" });
        }
        if (hasFinancial) {
            links.push({ to: "/reconciliation", label: "Reconciliation", icon: FileBarChart, tid: "nav-reconciliation" });
        }
    }

    // Owner/Head-only queues
    if (hasFinancial) {
        links.push({ to: "/admin/requisitions", label: "Requisitions", icon: ShoppingCart, tid: "nav-requisitions" });
        links.push({ to: "/admin/review-flags", label: "Flags", icon: AlertTriangle, tid: "nav-review-flags" });
        links.push({ to: "/admin/fh-reports", label: "FH Reports", icon: FileBarChart, tid: "nav-fh-reports" });
        links.push({ to: "/admin/upload-centre", label: "Upload Centre", icon: Upload, tid: "nav-upload-centre" });
        links.push({ to: "/admin/import-summary", label: "Import", icon: FileBarChart, tid: "nav-import-summary" });
    }

    // Admin / PM shared
    if (isAdmin || isPM) {
        links.push({ to: "/admin/expense-dashboard", label: "Spend", icon: PieChart, tid: "nav-expense-dashboard" });
        links.push({ to: "/admin/purchase", label: "Purchase", icon: ShoppingCart, tid: "nav-purchase" });
    }

    // Admin-only management
    if (isAdmin) {
        links.push({ to: "/admin/users", label: "Users", icon: Users, tid: "nav-users" });
        links.push({ to: "/admin/templates", label: "Templates", icon: FileText, tid: "nav-templates" });
        links.push({ to: "/admin/vendors", label: "Vendors", icon: Wallet, tid: "nav-vendors" });
        links.push({ to: "/admin/procurement", label: "Planner", icon: ShoppingCart, tid: "nav-procurement-planner" });
        links.push({ to: "/admin/simulator", label: "Simulator", icon: Beaker, tid: "nav-simulator" });
        links.push({ to: "/admin/catalog", label: "Catalog", icon: Boxes, tid: "nav-catalog" });
        links.push({ to: "/admin/presets", label: "Presets", icon: MessageSquare, tid: "nav-presets" });
        links.push({ to: "/admin/sla-editor", label: "SLA Editor", icon: ScrollText, tid: "nav-sla-editor" });
        links.push({ to: "/admin/absence-rules", label: "Rules", icon: Shield, tid: "nav-absence-rules" });
        links.push({ to: "/admin/storage", label: "Storage", icon: HardDrive, tid: "nav-storage" });
        links.push({ to: "/admin/outbox", label: "Outbox", icon: Inbox, tid: "nav-outbox" });
        links.push({ to: "/admin/reports", label: "Reports", icon: FileBarChart, tid: "nav-reports" });
        links.push({ to: "/admin/audit-log", label: "Audit", icon: ScrollText, tid: "nav-audit" });
        links.push({ to: "/admin/backups", label: "Backups", icon: Database, tid: "nav-backups" });
        links.push({ to: "/admin/expenses/import", label: "Import expenses", icon: Upload, tid: "nav-expense-import" });
    }

    // Desktop split: primary chips shown inline, rest into "More ▾" dropdown.
    // Width is measured live so the right-side account cluster (with LOGOUT)
    // is NEVER pushed off-screen at any viewport width (Safari flex-gap safe).
    const navContainerRef = useRef(null);
    const [primaryCount, setPrimaryCount] = useState(4);
    useEffect(() => {
        const el = navContainerRef.current;
        if (!el) return;
        function recompute() {
            const w = el.clientWidth || 0;
            // Each chip ≈ 110px including gap; reserve 90px for the More ▾ pill.
            const usable = Math.max(0, w - 90);
            const n = Math.min(links.length, Math.max(1, Math.floor(usable / 110)));
            setPrimaryCount(n);
        }
        recompute();
        let ro = null;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(recompute);
            ro.observe(el);
        }
        window.addEventListener("resize", recompute);
        return () => { if (ro) ro.disconnect(); window.removeEventListener("resize", recompute); };
    }, [links.length]);
    const primaryLinks = links.slice(0, primaryCount);
    const overflowLinks = links.slice(primaryCount);

    // Auto-scroll active chip into view on mobile strip whenever route changes
    useEffect(() => {
        const strip = stripRef.current;
        if (!strip) return;
        const active = strip.querySelector('[data-active="true"]');
        if (active && active.scrollIntoView) {
            try {
                active.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
            } catch {
                /* older browsers */
            }
        }
    }, [location.pathname, links.length]);

    // Cmd/Ctrl-K global search (Owner / Head / PM only)
    useEffect(() => {
        const canSearch = hasFinancial || isPM;
        if (!canSearch) return;
        function handler(e) {
            if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
                e.preventDefault();
                setSearchOpen((v) => !v);
            }
        }
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [hasFinancial, isPM]);

    function isChipActive(link) {
        if (link.end) return location.pathname === link.to;
        return location.pathname === link.to || location.pathname.startsWith(link.to + "/");
    }

    return (
        <div className="min-h-screen bg-[#F4F6F9] text-gray-900">
            <header className="sticky top-0 z-40 bg-[#1E3355] text-white fx-shadow-sticky" data-testid="app-header">
                {/* --- Row 1: brand + desktop nav + user actions --- */}
                <div className="mx-auto max-w-7xl px-3 sm:px-6 h-14 flex items-center gap-3">
                    <Link to={isClient ? "/client" : "/"} className="flex items-center gap-2 shrink-0" data-testid="brand-link">
                        <div className="w-7 h-7 rounded-md bg-[#E02B2B] flex items-center justify-center font-display font-bold text-sm">
                            F
                        </div>
                        <div className="leading-tight">
                            <div className="font-display font-bold text-sm sm:text-base">Flexworx Ops</div>
                            <div className="text-[10px] sm:text-xs text-white/70 -mt-0.5 hidden sm:block">A Flexworx Company</div>
                        </div>
                    </Link>

                    {/* Desktop primary chips */}
                    <nav ref={navContainerRef} className="hidden md:flex items-center gap-1.5 flex-1 min-w-0 overflow-hidden" data-testid="desktop-nav">
                        {primaryLinks.map((l) => (
                            <NavLink
                                key={l.tid}
                                to={l.to}
                                end={l.end}
                                data-testid={l.tid}
                                className={({ isActive }) => "fx-nav-chip"}
                                data-active={isChipActive(l)}
                            >
                                <l.icon className="w-4 h-4" />
                                <span>{l.label}</span>
                            </NavLink>
                        ))}
                        {overflowLinks.length > 0 && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button className="fx-nav-chip" data-testid="nav-more">
                                        <MoreHorizontal className="w-4 h-4" />
                                        <span>More</span>
                                        <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="max-h-[70vh] overflow-y-auto w-56">
                                    {overflowLinks.map((l) => (
                                        <DropdownMenuItem
                                            key={l.tid}
                                            data-testid={l.tid + "-more"}
                                            onSelect={(e) => { e.preventDefault(); nav(l.to); }}
                                            className={isChipActive(l) ? "bg-gray-100 font-semibold" : ""}
                                        >
                                            <l.icon className="w-4 h-4 mr-2 text-[#1E3355]" />
                                            {l.label}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </nav>

                    {/* Right cluster — ALWAYS visible; logout & controls pinned here.
                        Kept compact (SyncPill + LanguageSwitcher + single User ▾ menu)
                        so it never overflows off-screen even at 1280px width. */}
                    <div className="flex items-center gap-1.5 shrink-0 ml-auto">
                        <SyncPill />
                        <LanguageSwitcher />
                        <AnnouncementBell />
                        {(hasFinancial || isPM) && (
                            <button
                                onClick={() => setSearchOpen(true)}
                                className="p-2 rounded-md text-white/80 hover:text-white hover:bg-white/10"
                                data-testid="header-search"
                                title="Global search (Ctrl/⌘-K)"
                                aria-label="Open global search"
                            >
                                <Search className="w-4 h-4" />
                            </button>
                        )}
                        {/* User menu — consolidates help / change password / logout so
                            individual icons don't push the logout off-screen on Safari. */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    className="inline-flex items-center gap-1.5 pl-2 pr-2 py-1.5 rounded-md text-white/95 hover:bg-white/10 border border-white/15"
                                    data-testid="header-user-menu"
                                    aria-label="User menu"
                                    title={user?.name || user?.email}
                                >
                                    <div className="w-6 h-6 rounded-full bg-[#E02B2B] text-white text-[10px] font-bold flex items-center justify-center">
                                        {(user?.name || user?.email || "?").trim().charAt(0).toUpperCase()}
                                    </div>
                                    <div className="hidden lg:block text-left leading-tight">
                                        <div className="text-xs text-white truncate max-w-[8rem]">{user?.name || user?.email}</div>
                                        <div className="text-[10px] text-white/60 uppercase tracking-wider">{(effectiveRole || role || "").replace("_", " ")}</div>
                                    </div>
                                    <ChevronDown className="w-3.5 h-3.5 opacity-80" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <div className="px-2 py-2 border-b border-gray-100">
                                    <div className="text-xs font-semibold text-[#1E3355] truncate">{user?.name}</div>
                                    <div className="text-[10px] text-gray-500 truncate">{user?.email}</div>
                                    <div className="text-[10px] text-gray-500 mt-0.5 uppercase tracking-wider">{(effectiveRole || role || "").replace("_", " ")}</div>
                                </div>
                                <DropdownMenuItem onSelect={() => setHelpOpen(true)} data-testid="header-help">
                                    <HelpCircle className="w-4 h-4 mr-2 text-[#1E3355]" /> Help &amp; guide
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => nav("/change-password")} data-testid="header-change-password">
                                    <KeyRound className="w-4 h-4 mr-2 text-[#1E3355]" /> Change password
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={doLogout} className="text-[#E02B2B]" data-testid="header-logout">
                                    <LogOut className="w-4 h-4 mr-2" /> Log out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        {/* Mobile: full-list drawer trigger (secondary — chip strip is primary) */}
                        <button
                            className="md:hidden p-2 rounded-md text-white/80 hover:bg-white/10"
                            onClick={() => setDrawerOpen(true)}
                            data-testid="mobile-menu-toggle"
                            aria-label="Open full menu"
                        >
                            <Menu className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* --- Row 2 (mobile only): horizontal scrollable chip strip = the "slider" --- */}
                {links.length > 0 && (
                    <div className="md:hidden border-t border-white/10">
                        <div
                            ref={stripRef}
                            className="fx-chip-strip fx-chip-fade"
                            data-testid="mobile-nav-strip"
                        >
                            {links.map((l) => (
                                <NavLink
                                    key={l.tid}
                                    to={l.to}
                                    end={l.end}
                                    data-testid={l.tid + "-mobile"}
                                    className="fx-nav-chip"
                                    data-active={isChipActive(l)}
                                >
                                    <l.icon className="w-4 h-4" />
                                    <span>{l.label}</span>
                                </NavLink>
                            ))}
                        </div>
                    </div>
                )}
            </header>

            {/* Full-list mobile drawer (accessible via hamburger) — mirrors desktop More */}
            {drawerOpen && (
                <div
                    className="md:hidden fixed inset-0 z-50 bg-black/50"
                    onClick={() => setDrawerOpen(false)}
                    data-testid="mobile-drawer-backdrop"
                >
                    <div
                        className="absolute right-0 top-0 h-full w-72 bg-white shadow-xl flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="mobile-menu"
                    >
                        <div className="h-14 flex items-center justify-between px-4 border-b border-gray-200">
                            <div className="text-sm font-semibold text-[#1E3355]">All navigation</div>
                            <button
                                onClick={() => setDrawerOpen(false)}
                                className="p-2 -mr-2 rounded-md hover:bg-gray-100"
                                data-testid="mobile-drawer-close"
                                aria-label="Close menu"
                            >
                                <X className="w-5 h-5 text-gray-600" />
                            </button>
                        </div>
                        <nav className="flex-1 overflow-y-auto py-2">
                            {links.map((l) => (
                                <NavLink
                                    key={l.tid}
                                    to={l.to}
                                    end={l.end}
                                    onClick={() => setDrawerOpen(false)}
                                    data-testid={l.tid + "-drawer"}
                                    className={({ isActive }) =>
                                        `flex items-center gap-3 px-4 py-3 text-sm border-l-4 ${
                                            isChipActive(l)
                                                ? "bg-red-50 border-[#E02B2B] text-[#1E3355] font-semibold"
                                                : "border-transparent text-gray-700 hover:bg-gray-50"
                                        }`
                                    }
                                >
                                    <l.icon className="w-4 h-4 text-[#1E3355]" />
                                    <span>{l.label}</span>
                                </NavLink>
                            ))}
                        </nav>
                        <div className="border-t border-gray-200 px-4 py-3 text-xs text-gray-500 leading-tight">
                            <div className="font-medium text-gray-700 truncate">{user?.name}</div>
                            <div className="uppercase tracking-wider text-[10px] text-gray-400">
                                {(effectiveRole || role || "").replace("_", " ")}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <PageBar />
            <HelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} />
            <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
            <main className="pb-16 md:pb-0"><ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary></main>
            <BottomTabBar />
        </div>
    );
}

function LanguageSwitcher() {
    const { user, refreshMe } = useAuth();
    const [busy, setBusy] = useState(false);
    async function change(lang) {
        if (lang === (user?.language || "en")) return;
        setBusy(true);
        try {
            await api.patch("/auth/me/language", { language: lang });
            if (refreshMe) await refreshMe();
            toast.success("Language updated");
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    }
    return (
        <div className="relative" data-testid="lang-switcher-wrap">
            <select value={user?.language || "en"} onChange={(e) => change(e.target.value)} disabled={busy}
                className="text-xs rounded-md bg-white/10 text-white border border-white/20 pl-6 pr-2 py-1.5 appearance-none cursor-pointer" data-testid="lang-switcher">
                {AVAILABLE_LANGUAGES.map((l) => <option key={l.code} value={l.code} className="text-gray-900">{l.label}</option>)}
            </select>
            <Languages className="w-3.5 h-3.5 absolute left-1.5 top-1/2 -translate-y-1/2 text-white/70 pointer-events-none" />
        </div>
    );
}
